import { CheckCircle2 } from "lucide-react"
import { forwardRef } from "react"
import { useTranslation } from "react-i18next"

/**
 * OKS-56 — Creator-side "Completed on {date}" affirmation block.
 *
 * Replaces the Mark-complete CTA on the Show page when the current
 * membership has already completed the masterclass (UX Spec §2 +
 * §6 a11y). Static — no action, no button.
 *
 * Focus management (UX Spec §6 — SR users should hear the
 * confirmation after `Mark as complete` succeeds):
 *   - The parent Show page transitions `completion` from `null` → row
 *     after the Inertia POST returns. A one-shot effect tied to that
 *     transition calls `.focus()` on the ref forwarded here.
 *   - `tabIndex={-1}` allows programmatic focus without inserting the
 *     block into the natural Tab order. The wrapper is announced as a
 *     `role="status"` region for SR pickup on focus.
 */
export interface CreatorMasterclassCompletedBlockProps {
  /** ISO 8601 timestamp the row was completed. */
  completedAt: string
}

export const CreatorMasterclassCompletedBlock = forwardRef<
  HTMLDivElement,
  CreatorMasterclassCompletedBlockProps
>(function CreatorMasterclassCompletedBlock({ completedAt }, ref) {
  const { t, i18n } = useTranslation()

  const date = new Date(completedAt)
  const formatted = date.toLocaleDateString(
    i18n.language === "en" ? "en-US" : "fr-FR",
    { day: "numeric", month: "short", year: "numeric" },
  )

  return (
    <div
      ref={ref}
      role="status"
      tabIndex={-1}
      data-testid="learn-completed-block"
      className="bg-muted/50 flex items-center gap-3 rounded-md border p-4 focus:outline-none"
    >
      <CheckCircle2
        className="size-6 shrink-0 text-emerald-600"
        aria-hidden="true"
      />
      <p className="text-sm font-medium">
        {t("learn.show.completed_on", { date: formatted })}
      </p>
    </div>
  )
})
